import {useMemo, useRef, useState} from 'react'
import {useDialogStore} from '@ariakit/react'
import dayjs from 'dayjs'
import {AxiosError} from 'axios'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Download01Icon} from '@/components/ui/icon/Icon'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {buildFile, downloadFile} from '@/utilities/helpers'
import {usePermissions} from '@/features/auth/store/store'
import {
    SiarBulkExportType,
    SiarExportPreviewItem,
    SiarExportProgressStatus,
    SiarPreviewStatusFilter
} from '@/features/projects/siar-bulk-export/types'
import {NoEligibleProjectsError} from '@/features/projects/siar-bulk-export/http/siarBulkExport.http'
import {useStoreSiarBulkExport} from '@/features/projects/siar-bulk-export/queries/useStoreSiarBulkExport'
import {useGetSiarBulkExportPreview} from '@/features/projects/siar-bulk-export/queries/useGetSiarBulkExportPreview'
import {useGetSiarBulkExportPreviewCounters} from '@/features/projects/siar-bulk-export/queries/useGetSiarBulkExportPreviewCounters'
import {siarExportTypeToNoEligibleProjectsLabel} from '@/features/projects/siar-bulk-export/utils'
import {
    BulkExportConfig,
    BulkSiarExportConfigStep
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportConfigStep'
import {BulkSiarExportPreviewStep} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportPreviewStep'
import {
    BulkSiarExportResultStep,
    SkippedProject
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportResultStep'

type DialogStep = 'config' | 'preview' | 'result'

export type PreviewFilterTab = 'all' | 'to_print' | 'to_export' | 'to_transmit' | 'excluded'

const tabToStatus: Record<PreviewFilterTab, SiarPreviewStatusFilter | undefined> = {
    all: undefined,
    to_print: 'to_print',
    to_export: 'to_export',
    to_transmit: 'to_transmit',
    excluded: 'excluded'
}

// Selection is tracked independently per selectable progress status so a "select/deselect all"
// on one tab never leaks into the others.
type SelectableStatus = SiarExportProgressStatus
const SELECTABLE_STATUSES: SelectableStatus[] = [...SiarExportProgressStatus.options]

type StatusSelection = {selectAll: boolean; ids: Set<string>}
type SelectionState = Record<SelectableStatus, StatusSelection>

const createInitialSelection = (): SelectionState => ({
    to_print: {selectAll: true, ids: new Set()},
    to_export: {selectAll: true, ids: new Set()},
    to_transmit: {selectAll: true, ids: new Set()}
})

const cloneSelection = (selection: SelectionState): SelectionState => ({
    to_print: {selectAll: selection.to_print.selectAll, ids: new Set(selection.to_print.ids)},
    to_export: {selectAll: selection.to_export.selectAll, ids: new Set(selection.to_export.ids)},
    to_transmit: {selectAll: selection.to_transmit.selectAll, ids: new Set(selection.to_transmit.ids)}
})

const isSelectableStatus = (tab: PreviewFilterTab): tab is SelectableStatus =>
    tab === 'to_print' || tab === 'to_export' || tab === 'to_transmit'

const initialConfig: BulkExportConfig = {
    exportType: null,
    dateFrom: null,
    dateTo: null,
    addRegistry: true
}

const formatPayloadDate = (date: Date | null): string | undefined =>
    date ? dayjs(date).format('YYYY-MM-DD') : undefined

const buildExportFileName = (exportType: SiarBulkExportType, dateFrom: Date | null, dateTo: Date | null): string => {
    const prefix = exportType === 'open_project' ? 'SIAR-Apertura' : 'SIAR-Chiusura'
    const fmt = (d: Date) => dayjs(d).format('DDMMYYYY')
    if (dateFrom && dateTo) return `${prefix}-${fmt(dateFrom)}-${fmt(dateTo)}`
    if (dateFrom) return `${prefix}-${fmt(dateFrom)}`
    if (dateTo) return `${prefix}-${fmt(dateTo)}`
    return prefix
}

type BulkSiarExportDialogProps = {
    onShowDetail?: (exportId: string) => void
}

export const BulkSiarExportDialog = ({onShowDetail}: BulkSiarExportDialogProps = {}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const [isOpen, setIsOpen] = useState(false)
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: setIsOpen
    })

    if (!permissions?.projects?.write) {
        return null
    }

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    <Button variant="primaryNeutral" onClick={() => setIsOpen(true)}>
                        <Download01Icon />
                        {t('projects:bulk_siar_export_title')}
                    </Button>
                }
            />
            {isOpen && (
                <BulkSiarExportDialogShell onClose={() => dialogStore.setOpen(false)} onShowDetail={onShowDetail} />
            )}
        </DialogProvider>
    )
}

const BulkSiarExportDialogShell = ({
    onClose,
    onShowDetail
}: {
    onClose: () => void
    onShowDetail?: (exportId: string) => void
}) => {
    const {t} = useTranslation()
    const [step, setStep] = useState<DialogStep>('config')
    const [config, setConfig] = useState<BulkExportConfig>(initialConfig)
    const [activeTab, setActiveTab] = useState<PreviewFilterTab>('all')
    const [onlyOpeningTransmitted, setOnlyOpeningTransmitted] = useState(false)
    // Per-status selection. Each block: selectAll=true → ids are the EXCLUDED projects (everything
    // else of that status is selected); selectAll=false → ids are the INCLUDED projects.
    const [selection, setSelection] = useState<SelectionState>(createInitialSelection)
    const [pendingCount, setPendingCount] = useState(0)
    const [createdExportId, setCreatedExportId] = useState<string>('')
    const [createdFileName, setCreatedFileName] = useState<string>('')
    const [skippedProjects, setSkippedProjects] = useState<SkippedProject[]>([])
    // Running map of projectId → projectCode built from every loaded preview page, used to
    // resolve the codes of skipped (non-exportable) projects returned by the export header.
    const projectCodeById = useRef<Map<string, number>>(new Map())
    // projectId → progress status, accumulated from loaded pages. Used to route the "in other
    // batch" project IDs (which arrive without a status) to the correct selection block.
    const projectStatusById = useRef<Map<string, SiarExportProgressStatus>>(new Map())

    const isPreviewReady = step === 'preview' && !!config.exportType && (!!config.dateFrom || !!config.dateTo)

    // The "only opening transmitted" filter is meaningful only for closing exports.
    const isClosingExport = config.exportType === 'close_project'
    const onlyOpeningTransmittedParam = isClosingExport && onlyOpeningTransmitted ? true : undefined

    const previewQuery = useGetSiarBulkExportPreview(
        {
            exportType: config.exportType ?? 'open_project',
            dateFrom: formatPayloadDate(config.dateFrom),
            dateTo: formatPayloadDate(config.dateTo),
            status: tabToStatus[activeTab],
            onlyOpeningTransmitted: onlyOpeningTransmittedParam
        },
        {
            enabled: isPreviewReady
        }
    )

    const countersQuery = useGetSiarBulkExportPreviewCounters(
        {
            exportType: config.exportType ?? 'open_project',
            dateFrom: formatPayloadDate(config.dateFrom),
            dateTo: formatPayloadDate(config.dateTo),
            onlyOpeningTransmitted: onlyOpeningTransmittedParam
        },
        {
            enabled: isPreviewReady
        }
    )

    // Accumulate codes from every loaded preview item (idempotent map writes) so skipped
    // project IDs can be shown as PRI codes in the result step regardless of the active tab.
    for (const item of previewQuery.remappedData) {
        projectCodeById.current.set(item.projectId, item.projectCode)
        projectStatusById.current.set(item.projectId, item.progressStatus)
    }

    const eligibleByStatus: Record<SelectableStatus, number> = {
        to_print: countersQuery.data?.toPrint ?? 0,
        to_export: countersQuery.data?.toExport ?? 0,
        to_transmit: countersQuery.data?.toTransmit ?? 0
    }
    const eligibleTotal = eligibleByStatus.to_print + eligibleByStatus.to_export + eligibleByStatus.to_transmit

    const selectedCountByStatus = (status: SelectableStatus): number => {
        const {selectAll, ids} = selection[status]
        return selectAll ? Math.max(0, eligibleByStatus[status] - ids.size) : ids.size
    }
    const selectedCount = SELECTABLE_STATUSES.reduce((sum, status) => sum + selectedCountByStatus(status), 0)

    // Selection check for a loaded preview item: its status is known, so look up its own block.
    const isSelected = (item: SiarExportPreviewItem): boolean => {
        const {selectAll, ids} = selection[item.progressStatus]
        return selectAll ? !ids.has(item.projectId) : ids.has(item.projectId)
    }

    // Selection check by bare project ID (for "in other batch" IDs, which arrive without a status).
    // Uses the accumulated status map; if the project is not loaded yet, approximate it as selected
    // while any view is still in select-all mode (matching the default all-selected state).
    const isProjectIdSelected = (projectId: string): boolean => {
        const status = projectStatusById.current.get(projectId)
        if (status) {
            const {selectAll, ids} = selection[status]
            return selectAll ? !ids.has(projectId) : ids.has(projectId)
        }
        return SELECTABLE_STATUSES.some(status => selection[status].selectAll && !selection[status].ids.has(projectId))
    }

    // Counts/totals scoped to the visible tab ("all" → aggregate; status tab → that status only).
    const viewSelectedCount = isSelectableStatus(activeTab) ? selectedCountByStatus(activeTab) : selectedCount
    const viewEligibleTotal = isSelectableStatus(activeTab) ? eligibleByStatus[activeTab] : eligibleTotal

    const inOtherBatchProjectIds = countersQuery.data?.inOtherBatchProjectIds ?? []
    const selectedInOtherBatchCount = inOtherBatchProjectIds.filter(isProjectIdSelected).length
    const mutation = useStoreSiarBulkExport({
        onSuccess: data => {
            if (!config.exportType) return
            const baseName = buildExportFileName(config.exportType, config.dateFrom, config.dateTo)
            const file = buildFile({
                extension: 'zip',
                fileName: baseName,
                data: data.blob
            })
            downloadFile(file)
            // Skipped (non-exportable) projects are surfaced in the result step, resolved to
            // their PRI code when known, otherwise falling back to the raw project ID.
            setSkippedProjects(
                data.skippedProjectIds.map(id => ({
                    projectId: id,
                    projectCode: projectCodeById.current.get(id) ?? null
                }))
            )
            const submittedCount = selectedCount
            setPendingCount(Math.max(0, submittedCount - data.skippedProjectIds.length))
            setCreatedExportId(data.exportId)
            setCreatedFileName(`${baseName}.zip`)
            setStep('result')
        },
        onError: (error: AxiosError<NoEligibleProjectsError>) => {
            const body = error.response?.data
            if (body?.code === 'NO_ELIGIBLE_PROJECTS' && config.exportType) {
                toast.error(t(siarExportTypeToNoEligibleProjectsLabel[config.exportType]))
                return
            }
            toast.error(t('errors:default'))
        }
    })

    // Toggle a single item's membership in its status block. selectAll=true → ids are exceptions
    // (toggling adds/removes an exclusion); selectAll=false → ids are inclusions. Either way: flip.
    const handleToggle = (item: SiarExportPreviewItem) => {
        setSelection(prev => {
            const next = cloneSelection(prev)
            const block = next[item.progressStatus]
            if (block.ids.has(item.projectId)) block.ids.delete(item.projectId)
            else block.ids.add(item.projectId)
            return next
        })
    }

    // Force a set of projects to selected/deselected. The "in other batch" IDs span statuses and
    // arrive without one, so route each to its known block (or apply to every block when unknown —
    // harmless, since the BE ignores IDs not eligible for a given status).
    const applySelectionToProjects = (projectIds: string[], shouldSelect: boolean) => {
        setSelection(prev => {
            const next = cloneSelection(prev)
            for (const projectId of projectIds) {
                const known = projectStatusById.current.get(projectId)
                const targets: SelectableStatus[] = known ? [known] : SELECTABLE_STATUSES
                for (const status of targets) {
                    const block = next[status]
                    // selected ⇔ (selectAll ? not-in-ids : in-ids). Derive the desired membership.
                    const shouldBeInIds = shouldSelect ? !block.selectAll : block.selectAll
                    if (shouldBeInIds) block.ids.add(projectId)
                    else block.ids.delete(projectId)
                }
            }
            return next
        })
    }

    // Master "select/deselect all" scoped to the active view: a status tab toggles ONLY its block,
    // the "all" tab toggles every block. Direction follows the current count (nothing selected →
    // select all; otherwise → deselect all), matching the toggle button label.
    const handleMasterToggle = () => {
        if (isSelectableStatus(activeTab)) {
            const status = activeTab
            const selectAll = selectedCountByStatus(status) === 0
            setSelection(prev => {
                const next = cloneSelection(prev)
                next[status] = {selectAll, ids: new Set()}
                return next
            })
            return
        }
        const selectAll = selectedCount === 0
        setSelection({
            to_print: {selectAll, ids: new Set()},
            to_export: {selectAll, ids: new Set()},
            to_transmit: {selectAll, ids: new Set()}
        })
    }

    const handleIncludeOtherBatch = () => {
        applySelectionToProjects(inOtherBatchProjectIds, true)
    }

    const handleIgnoreOtherBatch = () => {
        applySelectionToProjects(inOtherBatchProjectIds, false)
    }

    // Toggling the filter changes the eligible set, so reset selection to keep counts consistent.
    const handleOnlyOpeningTransmittedChange = (value: boolean) => {
        setOnlyOpeningTransmitted(value)
        setSelection(createInitialSelection())
    }

    const handleGoToPreview = () => {
        setSelection(createInitialSelection())
        setActiveTab('all')
        setOnlyOpeningTransmitted(false)
        setStep('preview')
    }

    const handleBackToConfig = () => {
        setStep('config')
    }

    const buildStatusSelectionPayload = (status: SelectableStatus) => {
        const {selectAll, ids} = selection[status]
        return {
            selectAll,
            selectedProjectIds: selectAll ? [] : Array.from(ids),
            exceptedProjectIds: selectAll ? Array.from(ids) : []
        }
    }

    const handleExport = () => {
        if (!config.exportType) return
        mutation.mutate({
            exportType: config.exportType,
            dateFrom: formatPayloadDate(config.dateFrom),
            dateTo: formatPayloadDate(config.dateTo),
            selections: {
                to_print: buildStatusSelectionPayload('to_print'),
                to_export: buildStatusSelectionPayload('to_export'),
                to_transmit: buildStatusSelectionPayload('to_transmit')
            },
            addRegistry: config.addRegistry
        })
    }

    const dialogWidth = useMemo(() => (step === 'preview' ? '1260px' : '670px'), [step])

    return (
        <Dialog width={dialogWidth}>
            <DialogHeader>
                <DialogHeading>{t('projects:bulk_siar_export_title')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            {step === 'config' && (
                <BulkSiarExportConfigStep config={config} onChange={setConfig} onNext={handleGoToPreview} />
            )}
            {step === 'preview' && (
                <BulkSiarExportPreviewStep
                    config={config}
                    items={previewQuery.remappedData}
                    total={previewQuery.total}
                    isLoading={previewQuery.isLoading}
                    counters={countersQuery.data}
                    isCountersLoading={countersQuery.isLoading}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    onlyOpeningTransmitted={onlyOpeningTransmitted}
                    onOnlyOpeningTransmittedChange={handleOnlyOpeningTransmittedChange}
                    isSelected={isSelected}
                    selectedCount={selectedCount}
                    viewSelectedCount={viewSelectedCount}
                    viewEligibleTotal={viewEligibleTotal}
                    onMasterToggle={handleMasterToggle}
                    onToggle={handleToggle}
                    onIncludeOtherBatch={handleIncludeOtherBatch}
                    onIgnoreOtherBatch={handleIgnoreOtherBatch}
                    selectedInOtherBatchCount={selectedInOtherBatchCount}
                    onLoadMore={() => {
                        if (previewQuery.hasNextPage && !previewQuery.isFetchingNextPage) {
                            void previewQuery.fetchNextPage()
                        }
                    }}
                    onBack={handleBackToConfig}
                    onExport={handleExport}
                    isExportPending={mutation.isPending}
                />
            )}
            {step === 'result' && (
                <BulkSiarExportResultStep
                    pendingCount={pendingCount}
                    fileName={createdFileName}
                    skippedProjects={skippedProjects}
                    onClose={onClose}
                    onGoToGroup={
                        onShowDetail && createdExportId
                            ? () => {
                                  onClose()
                                  onShowDetail(createdExportId)
                              }
                            : undefined
                    }
                />
            )}
        </Dialog>
    )
}
